import React, { useEffect, useState } from 'react';
import { focusQuiet } from '../utils/scroll';
import { useAutoList } from '../hooks/useAutoList';
import { api } from '../api/client';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import { usePerm } from '../hooks/usePerm';
import { usePush } from '../hooks/usePush';
import { Analytics, track } from '../analytics';
import { useTranslation } from 'react-i18next';
import { useTranslatedTask } from '../hooks/useTranslatedTask';
import { useFormDraft, useUnsavedGuard } from '../hooks/useFormDraft';
import { requestMilestoneCheck } from '../hooks/useMilestone';
import TranslatedField from './TranslatedField';
import DraftRestoredBanner from './DraftRestoredBanner';
import UserCircle from './UserCircle';
import ClientEmailComposer from './ClientEmailComposer';
import ClientModal from './ClientModal';
import { backdropClose } from '../utils/modalBackdrop';
import { useCountryText } from '../hooks/useCountryText';
import TaskEquipmentPicker from './TaskEquipmentPicker';
import AssigneePicker from './AssigneePicker';
import PropertyPicker from './PropertyPicker';

// Режим инспекции в «Новой работе». Включён вместе с fill-runner'ом (InspectionRunner).
const INSPECTION_TOGGLE_ENABLED = true;

/**
 * Единый компонент создания и редактирования Работы.
 *
 * Пропы:
 *   propertyId  — если передан, объект зафиксирован (режим карточки объекта)
 *                 если не передан — показывает дропдаун выбора объекта (режим дашборда)
 *   task        — объект задачи для редактирования, null/undefined для создания
 *   onClose     — callback закрытия
 *   onSave(saved) — callback после успешного сохранения
 */
export default function TaskModal({ propertyId, task, onClose, onSave, onViewed, onDelete, demoMode = false, demoPrefill = null, demoSubmitLabel = null, prefill = null, externalRequestId = null, initialEquipmentIds = null }) {
  const { t, i18n } = useTranslation();
  const { ct } = useCountryText();

  // Скрываем FAB пока модал открыт
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('fab-hide'));
    return () => window.dispatchEvent(new CustomEvent('fab-show'));
  }, []);
  const { user } = useAuth();
  const translated = useTranslatedTask(task, i18n.language);
  const { isSupported, isSubscribed, permission, subscribe } = usePush();
  const ROLE_LABELS = {
    superadmin: t('roles.superadmin'),
    admin: t('roles.admin'),
    foreman: t('roles.foreman'),
    worker: t('roles.worker'),
  };
  // Модуль «Работы» мигрирован на RBAC: узкая область данных вместо «это воркер».
  const { isOwnScope, can } = usePerm();
  // Здесь isWorker = «работаю только со своими Работами»: влияет на
  // самоназначение исполнителем и на упрощённую форму.
  const isWorker = isOwnScope;
  // Работник может редактировать title/description только если сам создал задачу
  const workerCanEditMeta = !isWorker || !task?.id || task?.user_id === user?.id;
  const today = new Date().toISOString().split('T')[0];

  const [form, setForm] = useState(
    // Редактирование: нормализуем assignees → assignee_ids СРАЗУ в инициализаторе,
    // иначе baseline в useFormDraft снимется без assignee_ids, эффект допишет их
    // пост-маунт и форма ложно станет «грязной» (см. useFormDraft).
    task
      ? { ...task, assignee_ids: (task.assignees || []).map(a => a.id) }
      : {
          title: '', description: '', report_description: '',
          type: 'one_time', recurrence: '',
          scheduled_date: today, status: 'pending',
          assignee_ids: [],
          planning_type: 'visit',
          scheduled_time: '',
          date_from: '',
          reminder_before: 60,
          reminder_before_1: null,  // Таймер 1 (крупный масштаб): выкл по умолчанию
          notify_client: true,      // включать клиента в напоминания (можно исключить)
          // только для режима дашборда (без propertyId)
          property_id: propertyId || '',
          // демо-режиссура (Слайс 0): предзаполнение поверх дефолтов
          ...(demoPrefill || {}),
          // предзаполнение из внешней Заявки (Do Do Desk и пр.)
          ...(prefill || {}),
        }
  );
  // Причина переноса: только у Работы Проекта с активным планом (решение Павла 16.10.2026).
  // В списке флага нет — дозапрашиваем один раз при открытии на редактирование.
  const [planTracked, setPlanTracked] = useState(!!task?.plan_tracked);
  useEffect(() => {
    if (!task?.id || task.plan_tracked !== undefined) return undefined;
    let off = false;
    api.getTask(task.id).then(r => { if (!off) setPlanTracked(!!r.plan_tracked); }).catch(() => {});
    return () => { off = true; };
  }, [task?.id, task?.plan_tracked]);
  const datesChanged = !!task?.id && planTracked && (
    (form.scheduled_date || null) !== (task.scheduled_date || null)
    || (form.planning_type === 'work' && (form.date_from || null) !== (task.date_from || null)));
  const [reminderOpen, setReminderOpen] = useState(false);
  const [coarseCustom, setCoarseCustom] = useState(false); // открыт инпут «своё» у Таймера 1
  const [emailComposer, setEmailComposer] = useState(null); // 'completion' | 'invoice' | null
  const [editClient, setEditClient] = useState(null);       // карточка Клиента для быстрого добавления email

  // Открыть карточку Клиента (быстро добавить email из подсказки тумблера).
  const openClientEditor = async () => {
    if (!clientId) return;
    try {
      const client = await api.getClient(clientId);
      setEditClient(client);
    } catch (e) { toast.error(e.message); }
  };

  // Инспекция как режим Работы. type (разовая/повторяющ.) остаётся ортогональным.
  //
  // ⚠️ Маркером раньше служил сам checklist_template_id — то есть «инспекция» и
  // «есть чек-лист» были одним и тем же. Умная камера это разводит: инспекция
  // может идти вообще без шаблона, и тогда способ у неё один. Маркер теперь —
  // tasks.is_inspection, шаблон необязателен.
  const isExistingTask = !!task?.id;
  const [inspectionMode, setInspectionMode] = useState(
    !!(task && (task.is_inspection || task.checklist_template_id))
  );
  const [checklistTemplates, setChecklistTemplates] = useState([]);

  useEffect(() => {
    if (inspectionMode && !isWorker && !isExistingTask && checklistTemplates.length === 0) {
      const lang = i18n.language?.split('-')[0] || 'ru';
      api.get(`/checklists/templates?lang=${lang}`)
        .then(list => setChecklistTemplates(Array.isArray(list) ? list : []))
        .catch(() => {});
    }
  }, [inspectionMode, isWorker, isExistingTask, checklistTemplates.length, i18n.language]);
  const [reminderCustom, setReminderCustom] = useState(false);
  // «Своя» периодичность хранится строкой every:N:unit (unit: d|w|m|y) в том же
  // поле tasks.recurrence — без миграции. Пока N не введено, recurrence = '', поэтому
  // «режим своей» и черновик N/unit живут в локальном стейте.
  const [recCustomOpen, setRecCustomOpen] = useState(false);
  const [recCustomN, setRecCustomN] = useState('');
  const [recCustomUnit, setRecCustomUnit] = useState('w');
  const reminderRef = React.useRef(null);

  // Title autocomplete
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggActiveIndex, setSuggActiveIndex] = useState(-1);
  const titleInputRef = React.useRef(null);
  const suggestionsRef = React.useRef(null);
  const suggTimerRef = React.useRef(null);
  const [loading, setLoading] = useState(false);
  // Список доступных исполнителей грузит сам AssigneePicker (/api/team/assignable);
  // здесь только факт загрузки — от него зависит автоскролл демо-тура.
  const [assignableLoaded, setAssignableLoaded] = useState(false);
  const demoSubmitRef = React.useRef(null);

  // Демо (Слайс 0): автоскролл к кнопке «Назначить работу» после загрузки списка
  // исполнителей (от него зависит позиция кнопки) — чтобы юзеру не приходилось
  // догадываться свайпнуть вниз.
  useEffect(() => {
    if (!demoMode || !assignableLoaded) return;
    const id = setTimeout(() => {
      demoSubmitRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
    return () => clearTimeout(id);
  }, [demoMode, assignableLoaded]);
  const [properties, setProperties] = useState([]); // только если нет propertyId
  const [newPropertyOpen, setNewPropertyOpen] = useState(false);
  const [newPropertyForm, setNewPropertyForm] = useState({ name: '', address: '' });
  const [newPropertyLoading, setNewPropertyLoading] = useState(false);

  // Черновик: только режим создания. Привязка по объекту (или дашборд).
  const { restored, isDirty, clearDraft, resetForm } = useFormDraft({
    scope: `task:${propertyId || 'dashboard'}`,
    form, setForm, enabled: !task?.id,
  });
  // Guard (с обещанием «сохранится как черновик») — только режим создания,
  // где персист черновика реально включён. В edit-режиме черновика нет, поэтому
  // и подтверждение с этим текстом показывать нельзя.
  const guardedClose = useUnsavedGuard(onClose, isDirty && !task?.id, t('common.draftCloseConfirm'), 'task');
  const handleCancel = () => { clearDraft('cancel'); onClose(); };

  // Закрываем дропдаун напоминания по клику вне
  useEffect(() => {
    if (!reminderOpen) return;
    const handler = (e) => {
      if (reminderRef.current && !reminderRef.current.contains(e.target)) {
        setReminderOpen(false);
        setReminderCustom(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [reminderOpen]);

  // Close suggestions on outside click
  React.useEffect(() => {
    if (!showSuggestions) return;
    const handler = (e) => {
      if (
        titleInputRef.current && !titleInputRef.current.contains(e.target) &&
        suggestionsRef.current && !suggestionsRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSuggestions]);

  // Cleanup debounce on unmount
  React.useEffect(() => () => clearTimeout(suggTimerRef.current), []);

  const handleTitleChange = (e) => {
    const val = e.target.value;
    setForm(f => ({ ...f, title: val }));
    setSuggActiveIndex(-1);
    clearTimeout(suggTimerRef.current);
    if (val.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    suggTimerRef.current = setTimeout(async () => {
      try {
        const results = await api.getTaskTitleSuggestions(val.trim());
        if (results.length > 0) {
          setSuggestions(results);
          setShowSuggestions(true);
          track('task_title_suggestions_shown', { count: results.length });
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      } catch {}
    }, 200);
  };

  const handleTitleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSuggActiveIndex(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSuggActiveIndex(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && suggActiveIndex >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[suggActiveIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const selectSuggestion = (title) => {
    setForm(f => ({ ...f, title }));
    setShowSuggestions(false);
    setSuggestions([]);
    setSuggActiveIndex(-1);
    track('task_title_suggestion_selected', { title });
    focusQuiet(titleInputRef.current);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Режим «Работа»: начало не может быть позже дедлайна. Классическая опечатка —
  // не тот месяц/год у одной из дат. Меняем даты местами (ничего не теряем), а не
  // стираем одну из них. Вызываем на blur, а не на change: нативный date-input
  // стреляет onChange на каждой набранной цифре года (0002, 0020, 0202…), и
  // коррекция посреди ввода ломала бы набор.
  const fixWorkDates = (f) => {
    if (f.planning_type !== 'work' || !f.date_from || !f.scheduled_date) return f;
    if (f.date_from <= f.scheduled_date) return f;
    track('task_dates_autocorrected', { from: f.date_from, to: f.scheduled_date });
    return { ...f, date_from: f.scheduled_date, scheduled_date: f.date_from };
  };
  const normalizeWorkDates = () => {
    if (form.planning_type === 'work' && form.date_from && form.scheduled_date && form.date_from > form.scheduled_date) {
      setForm(fixWorkDates);
      toast(t('tasks.datesSwapped'), { icon: '↔️' });
    }
  };
  const setNP = (k, v) => setNewPropertyForm(f => ({ ...f, [k]: v }));

  // Автосписки: «1. » / «- » + Enter продолжают список
  // Оборудование, выбранное ДО сохранения Работы. Связь task_equipment требует
  // task_id, которого у новой Работы ещё нет, поэтому копим здесь и досылаем
  // сразу после создания.
  // initialEquipmentIds — Работу заводят ИЗ карточки оборудования: человек
  // стоит перед печью, отсканировал наклейку, прочитал историю и тут же
  // создаёт выезд. Единица должна быть выбрана заранее, иначе он выходит
  // искать её руками — то есть возвращается ровно к тому пути, от которого
  // сканирование уводило.
  const [pendingEquipment, setPendingEquipment] = useState(() => initialEquipmentIds || []);

  const descRef = useAutoList('task_description');
  const reportRef = useAutoList('task_report_description');

  const handleCreateProperty = async () => {
    if (!newPropertyForm.name.trim()) { toast.error(t('tasks.propertyNameRequired')); return; }
    setNewPropertyLoading(true);
    try {
      const created = await api.createProperty({ ...newPropertyForm, client_id: null });
      setProperties(ps => [...ps, created]);
      set('property_id', created.id);
      setPendingEquipment([]);   // Объект сменился — прежний выбор к нему не относится
      setNewPropertyOpen(false);
      setNewPropertyForm({ name: '', address: '' });
      toast.success(t('tasks.propertyCreated'));
    } catch (err) { toast.error(err.message); }
    finally { setNewPropertyLoading(false); }
  };

  // Инициализируем assignee_ids из существующих исполнителей при редактировании
  useEffect(() => {
    if (task?.assignees) {
      setForm(f => ({ ...f, assignee_ids: task.assignees.map(a => a.id) }));
    }
  }, [task]);

  // Исполнитель открыл задачу — отмечаем как просмотренную (любая роль)
  useEffect(() => {
    if (!task?.id) return;
    const isAssignee = task?.assignees?.some(a => a.id === user?.id);
    if (!isAssignee) return;
    api.markTaskViewed(task.id).then(() => {
      // Уведомляем родителя чтобы он обновил viewed_at у исполнителя локально
      if (onViewed) onViewed(task.id, user.id);
    }).catch(() => {});
  }, [task?.id]);

  // Загружаем объекты, если Работа создаётся не из карточки Объекта.
  // Исполнителей грузит AssigneePicker: раньше здесь был /settings/team, закрытый
  // team.view, — Бригадир получал 403 и пустой список.
  useEffect(() => {
    if (!propertyId) {
      api.getProperties().then(setProperties).catch(console.error);
    }
  }, [propertyId]);

  // Резолвим клиента по объекту (для тумблера «Уведомлять клиента» + быстрого
  // добавления email). resolvedClientEmail: undefined = ещё не знаем (не блокируем),
  // null = точно нет email, строка = есть. clientId — для открытия карточки Клиента.
  const [resolvedClientEmail, setResolvedClientEmail] = useState(undefined);
  const [clientId, setClientId] = useState(null);
  useEffect(() => {
    if (isWorker) return; // тумблер только у админа
    const pid = propertyId || form.property_id || task?.property_id;
    if (!pid) { setResolvedClientEmail(undefined); setClientId(null); return; }
    let cancelled = false;
    api.getProperty(pid)
      .then(p => {
        if (cancelled) return;
        setResolvedClientEmail(p?.client_email || null);
        setClientId(p?.client_id || null);
      })
      .catch(() => { if (!cancelled) { setResolvedClientEmail(undefined); setClientId(null); } });
    return () => { cancelled = true; };
  }, [propertyId, form.property_id, task?.property_id, isWorker]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Демо (Слайс 0, вариант A): «Назначить работу» ничего не пишет в БД —
    // показательная задача уже в сиде. Просто отдаём управление туру.
    if (demoMode) { clearDraft('saved'); onSave && onSave(form); return; }
    if (!propertyId && !form.property_id) { toast.error(t('tasks.propertyRequired')); return; }
    // Шаблон чек-листа больше не обязателен: инспекция без него — это инспекция
    // Умной камерой. Обязательным он был, пока чек-лист был единственным способом.
    setLoading(true);
    try {
      // Страховка: Enter в форме не вызывает blur, и перепутанные даты ушли бы на сервер.
      const fixedForm = fixWorkDates(form);
      const payload = propertyId
        ? { ...fixedForm, property_id: propertyId }
        : { ...fixedForm };
      if (!task?.id && isAwaitingForm) {
        payload.awaiting_client = true;
        payload.scheduled_date = null; payload.date_from = null; payload.scheduled_time = '';
      } else if (!task?.id) {
        delete payload.awaiting_client; delete payload.follow_up_date;
      }
      // Явный маркер инспекции. Без него Работа без шаблона сохранилась бы как
      // обычная, аккордеон «Инспекция объекта» не появился бы, и Умной камере
      // было бы негде жить.
      if (!isExistingTask) payload.is_inspection = inspectionMode ? 1 : 0;
      if (!task?.id && externalRequestId) payload.external_request_id = externalRequestId;
      // Смена «На согласовании» у существующей Работы — отдельными эндпоинтами ДО общего сохранения.
      if (task?.id && wasAwaiting && !isAwaitingForm) {
        if (!form.scheduled_date) { toast.error(t('tasks.awaiting.errDate')); setLoading(false); return; }
        await api.confirmTaskClient(task.id, payload.planning_type === 'work'
          ? { planning_type: 'work', scheduled_date: payload.scheduled_date, date_from: payload.date_from || undefined }
          : { planning_type: 'visit', scheduled_date: payload.scheduled_date, scheduled_time: payload.scheduled_time || undefined });
        track('task_client_confirmed', { src: 'edit_status', planning_type: payload.planning_type });
      } else if (task?.id && !wasAwaiting && isAwaitingForm) {
        await api.awaitTaskClient(task.id, form.follow_up_date);
        track('task_awaiting_set', { src: 'edit_status', has_follow_up: !!form.follow_up_date });
      }
      const saved = task?.id
        ? await api.updateTask(task.id, payload)
        : await api.createTask(payload);
      // Привязки, выбранные до сохранения. Отправляем последовательно и молча:
      // Работа уже создана, и падать из-за не доехавшей связи с печью нельзя —
      // человек её просто добавит в карточке.
      if (!task?.id && saved?.id && pendingEquipment.length) {
        for (const eqId of pendingEquipment) {
          try { await api.equipmentLink(saved.id, eqId); }
          catch (e) { console.error('[task] привязка оборудования не удалась:', e?.message); }
        }
        setPendingEquipment([]);
      }

      // Существующая Работа на согласовании: «Вернуться к Работе» живёт в своём эндпоинте.
      if (task?.id && wasAwaiting && isAwaitingForm && (form.follow_up_date || null) !== (task.follow_up_date || null)) {
        await api.setTaskFollowUp(task.id, form.follow_up_date);
        track('task_follow_up_changed', { src: 'modal', cleared: !form.follow_up_date });
      }
      if (!task?.id && isAwaitingForm) track('task_awaiting_set', { src: 'create_modal', has_follow_up: !!form.follow_up_date });
      task?.id ? Analytics.taskUpdated({ type: payload.type }) : Analytics.taskCreated({ status: payload.status, type: payload.type });
      clearDraft('saved');
      onSave(saved);
      window.dispatchEvent(new Event('tasks-updated'));
      toast.success(task?.id ? t('tasks.saved') : t('tasks.added'));

      // Мини-празднования: бэкенд посчитал рубеж синхронно и вернул milestone_hit.
      // Просим Layout забрать карточку — splash покажется поверх закрывшейся формы.
      if (!task?.id && saved?.milestone_hit) requestMilestoneCheck();

      // Web Push: предлагаем подписку работнику при назначении задачи
      // Условие: пуши поддерживаются, ещё не подписан, и текущий пользователь назначен
      const assignedIds = payload.assignee_ids || [];
      if (isSupported && !isSubscribed && user && assignedIds.includes(user.id)) {
        toast((toastRef) => (
          <span style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span>{t('tasks.pushPrompt')}</span>
            <span style={{ display: 'flex', gap: 8 }}>
              <button
                style={{ padding: '4px 12px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}
                onClick={async () => {
                  toast.dismiss(toastRef.id);
                  const result = await subscribe();
                  if (result.ok) toast.success(t('tasks.pushEnabled'));
                  else if (result.reason === 'permission_denied') toast.error(t('tasks.pushDenied'));
                }}
              >
                {t('tasks.pushEnable')}
              </button>
              <button
                style={{ padding: '4px 12px', background: 'transparent', border: '1px solid var(--border, #ddd)', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}
                onClick={() => toast.dismiss(toastRef.id)}
              >
                {t('tasks.pushLater')}
              </button>
            </span>
          </span>
        ), { duration: 10000, id: 'push-prompt' });
      }
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  const selectedIds = form.assignee_ids || [];

  // «На согласовании»: создаётся только разовая Работа и только с правом confirm_client.
  // У существующей Работы на согласовании блок дат скрыт (даты у неё NULL по инварианту);
  // «Вернуться к Работе» меняется отдельным эндпоинтом (PATCH /follow-up).
  const canAwait = can('tasks.confirm_client');
  const isAwaitingForm = !!form.awaiting_client;
  const wasAwaiting = !!task?.awaiting_client;
  // «На согласовании» доступно в поле «Статус»: при создании, у уже ждущей Работы и у
  // обычной Работы в статусе «Ожидает» (разовая, вне Проекта и AMC).
  const canPickAwaiting = canAwait && form.type === 'one_time'
    && (!task?.id || wasAwaiting || (task.status === 'pending' && !task.project_id && !task.has_amc));
  const showStatusField = !!task?.id || canPickAwaiting;
  const statusValue = isAwaitingForm ? 'awaiting' : form.status;
  const onStatusChange = (v) => {
    if (v === 'awaiting') {
      setForm(f => ({ ...f, awaiting_client: 1, status: 'pending' }));
    } else {
      // Выход из «На согласовании»: нужна дата — подставляем сегодняшнюю, фиксируется при сохранении.
      setForm(f => ({ ...f, awaiting_client: 0, status: v, scheduled_date: f.scheduled_date || today, planning_type: f.planning_type || 'visit' }));
    }
    track('task_awaiting_toggled', { on: v === 'awaiting', edit: !!task?.id });
  };
  const statusField = (
    <div className="form-group">
      <label className="form-label">{t('tasks.fieldStatus')}</label>
      <select className="form-select" value={statusValue} data-ph-id="task-status-select"
        onChange={e => onStatusChange(e.target.value)}>
        <option value="pending">{t('tasks.statusPending')}</option>
        {!!task?.id && <option value="in_progress">{t('tasks.statusInProgress')}</option>}
        {!!task?.id && <option value="done">{t('tasks.statusDone')}</option>}
        {canPickAwaiting && <option value="awaiting">{t('tasks.awaiting.badge')}</option>}
      </select>
    </div>
  );

  // Флаги для тумблера «Уведомлять клиента» (см. эффект resolvedClientEmail выше).
  const clientEmailKnown = resolvedClientEmail !== undefined;
  const clientHasEmail = !!resolvedClientEmail;
  const notifyClientDisabled = clientEmailKnown && !clientHasEmail;

  return (
    <div className="modal-overlay" {...backdropClose(guardedClose)}>
      <div className="modal">
        <div className="modal-header">
          <h3>{task?.id ? t('tasks.modalEdit') : t('tasks.modalNew')}</h3>
          <button className="modal-close" onClick={guardedClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {restored && <DraftRestoredBanner posthog="task_draft" onClear={() => { clearDraft('manual'); resetForm(); }} />}

            {/* Режим Работы: Обычная / Инспекция (только для не-Работника). Гейт до готовности fill-runner. */}
            {!isWorker && INSPECTION_TOGGLE_ENABLED && (
              <div className="form-group">
                <div style={{ display: 'flex', gap: 8 }}>
                  {['regular', 'inspection'].map(m => {
                    const isInsp = m === 'inspection';
                    const active = isInsp === inspectionMode;
                    return (
                      <button
                        key={m}
                        type="button"
                        disabled={isExistingTask}
                        onClick={() => {
                          if (isExistingTask) return;
                          setInspectionMode(isInsp);
                          if (!isInsp) set('checklist_template_id', null);
                          track('task_inspection_mode_changed', { inspection: isInsp });
                        }}
                        style={{
                          flex: 1, padding: '8px 0', borderRadius: 8, border: '1.5px solid',
                          borderColor: active ? 'var(--accent)' : 'var(--border, #e0e0e0)',
                          background: active ? 'rgba(79,142,247,0.12)' : 'transparent',
                          color: active ? 'var(--accent)' : 'var(--text2)',
                          fontWeight: active ? 600 : 400, fontSize: 14,
                          cursor: isExistingTask ? 'default' : 'pointer',
                          opacity: isExistingTask && !active ? 0.5 : 1, transition: 'all 0.15s',
                        }}
                      >
                        {t(isInsp ? 'tasks.modeInspection' : 'tasks.modeRegular')}
                      </button>
                    );
                  })}
                </div>

                {inspectionMode && (
                  <div style={{ marginTop: 10 }}>
                    <label className="form-label">{t('tasks.fieldChecklistTemplateOptional')}</label>
                    {isExistingTask ? (
                      <div style={{ color: 'var(--text2)', fontSize: 13 }}>{t('tasks.inspectionLocked')}</div>
                    ) : checklistTemplates.length === 0 ? (
                      <div style={{ color: 'var(--text2)', fontSize: 12 }}>{t('tasks.noChecklistTemplates')}</div>
                    ) : (
                      <select className="form-select" value={form.checklist_template_id || ''}
                        onChange={e => set('checklist_template_id', e.target.value ? Number(e.target.value) : null)}
                        data-ph-capture="task_checklist_template_picked">
                        <option value="">{t('tasks.noChecklistNeeded')}</option>
                        {checklistTemplates.map(tpl => (
                          <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                        ))}
                      </select>
                    )}
                    {/* Способ инспекции выбирает исполнитель на объекте, а не
                        админ при планировании: админ не знает, как воркеру
                        удобнее в конкретной квартире. Шаблон здесь лишь решает,
                        будет ли выбор вообще. */}
                    <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text3)', lineHeight: 1.4 }}>
                      {t('tasks.inspectionMethodHint')}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Выбор объекта — только в режиме дашборда */}
            {!propertyId && (
              <div className="form-group">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <label className="form-label" style={{ margin: 0 }}>{t('tasks.fieldProperty')}</label>
                  <button type="button" style={{
                    fontSize: 12, color: 'var(--green)', background: 'none', border: 'none',
                    cursor: 'pointer', padding: 0, fontWeight: 600,
                  }} onClick={() => setNewPropertyOpen(o => !o)}>
                    {newPropertyOpen ? t('tasks.cancelNewProperty') : t('tasks.newProperty')}
                  </button>
                </div>
                <PropertyPicker
                  value={form.property_id}
                  properties={properties}
                  onChange={(id) => {
                    set('property_id', id);
                    // ⚠️ Выбранное оборудование относилось к прошлому
                    // Объекту. Не сбросить — и в новую Работу уедет
                    // техника с чужого адреса (бэкенд её отобьёт, но
                    // человек увидит молча пропавшую привязку).
                    setPendingEquipment([]);
                  }} />
                {newPropertyOpen && (
                  <div style={{
                    marginTop: 10, padding: '12px 14px',
                    border: '1.5px solid var(--green)', borderRadius: 10,
                    background: 'rgba(52,199,123,0.04)',
                    display: 'flex', flexDirection: 'column', gap: 8,
                  }}>
                    <input className="form-input" value={newPropertyForm.name} onChange={e => setNP('name', e.target.value)}
                      placeholder={ct('tasks.propertyNamePlaceholder')} autoFocus />
                    <input className="form-input" value={newPropertyForm.address} onChange={e => setNP('address', e.target.value)}
                      placeholder={ct('tasks.propertyAddressPlaceholder')} />
                    <button type="button" className="btn btn-green" onClick={handleCreateProperty} disabled={newPropertyLoading}
                      style={{ alignSelf: 'flex-end' }}>
                      {newPropertyLoading ? <span className="spinner" /> : t('tasks.createProperty')}
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">{t('tasks.fieldTitle')}</label>
              {workerCanEditMeta ? (
                <div style={{ position: 'relative' }}>
                  <input
                    ref={titleInputRef}
                    className="form-input"
                    value={form.title}
                    onChange={handleTitleChange}
                    onKeyDown={handleTitleKeyDown}
                    onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                    required
                    placeholder={t('tasks.titlePlaceholder')}
                    autoComplete="off"
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <div
                      ref={suggestionsRef}
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0, right: 0,
                        background: 'var(--bg)',
                        border: '1px solid var(--border)',
                        borderRadius: 10,
                        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                        zIndex: 200,
                        overflow: 'hidden',
                      }}
                    >
                      {suggestions.map((s, i) => (
                        <button
                          key={s}
                          type="button"
                          onMouseDown={e => { e.preventDefault(); selectSuggestion(s); }}
                          style={{
                            display: 'block', width: '100%', textAlign: 'left',
                            padding: '10px 14px', border: 'none',
                            background: i === suggActiveIndex
                              ? 'color-mix(in srgb, var(--accent) 10%, transparent)'
                              : 'transparent',
                            color: i === suggActiveIndex ? 'var(--accent)' : 'var(--text)',
                            fontWeight: i === suggActiveIndex ? 500 : 400,
                            cursor: 'pointer', fontSize: 14,
                            borderBottom: i < suggestions.length - 1 ? '1px solid var(--border)' : 'none',
                            transition: 'background 0.1s',
                          }}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <TranslatedField
                  text={translated.displayTitle || form.title}
                  isTranslated={translated.isTranslated}
                  hasTranslation={translated.hasTranslation}
                  showingOriginal={translated.showingOriginal}
                  toggleOriginal={translated.toggleOriginal}
                  as="div"
                  style={{ fontSize: 15, fontWeight: 500, padding: '8px 0', opacity: 0.85 }}
                />
              )}
            </div>

            <div className="form-group">
              <label className="form-label">{t('tasks.fieldDescription')}</label>
              {workerCanEditMeta ? (
                <textarea
                  className="form-textarea"
                  value={form.description || ''}
                  onChange={e => set('description', e.target.value)}
                  ref={descRef}
                  placeholder={t('tasks.descriptionPlaceholder')}
                  data-ph-capture-attribute-field="task_description"
                />
              ) : (
                <TranslatedField
                  text={translated.displayDescription || form.description}
                  isTranslated={translated.isTranslated}
                  hasTranslation={translated.hasTranslation}
                  showingOriginal={translated.showingOriginal}
                  toggleOriginal={translated.toggleOriginal}
                  as="p"
                  style={{ fontSize: 14, color: 'var(--text, #333)', margin: 0, padding: '6px 0', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}
                  emptyText={<span style={{ opacity: 0.4, fontStyle: 'italic' }}>{t('tasks.descriptionPlaceholder')}</span>}
                />
              )}
            </div>

            {/* Какое оборудование чинили. Блок сам прячется на жилых Объектах
                и там, где парк ещё не заведён. */}
            <TaskEquipmentPicker
              taskId={isExistingTask ? task.id : null}
              propertyId={propertyId || form.property_id || task?.property_id}
              canEdit={workerCanEditMeta}
              pending={pendingEquipment}
              onPendingChange={setPendingEquipment}
            />

            {/* Отчёт/«после» — у инспекции его нет (фиксируем состояние, а не результат работ).
                При создании новой Работы тоже скрыт: отчёт пишут по итогам, не на старте. */}
            {!inspectionMode && isExistingTask && (
              <div className="form-group">
                <label className="form-label">
                  {t('tasks.fieldReport')}{' '}
                  <span style={{ fontWeight: 400, opacity: 0.55, fontSize: 12 }}>{t('tasks.reportHint')}</span>
                </label>
                <textarea
                  className="form-textarea"
                  value={form.report_description || ''}
                  onChange={e => set('report_description', e.target.value)}
                  ref={reportRef}
                  placeholder={t('tasks.reportPlaceholder')}
                  data-ph-capture-attribute-field="task_report_description"
                />
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('tasks.fieldType')}</label>
                <select className="form-select" value={form.type} onChange={e => set('type', e.target.value)}>
                  <option value="one_time">{t('tasks.typeOneTime')}</option>
                  <option value="recurring">{t('tasks.typeRecurring')}</option>
                  <option value="emergency">{t('tasks.typeEmergency')}</option>
                </select>
              </div>
              {showStatusField && form.type !== 'recurring' && statusField}
              {form.type === 'recurring' && (
                <div className="form-group">
                  <label className="form-label">{t('tasks.fieldRecurrence')}</label>
                  {(() => {
                    const PRESETS = ['weekly', 'monthly', 'quarterly', 'yearly'];
                    const m = /^every:(\d{1,3}):([dwmy])$/.exec(form.recurrence || '');
                    const isCustom = !!m || recCustomOpen;
                    const customN = m ? m[1] : recCustomN;
                    const customUnit = m ? m[2] : recCustomUnit;
                    const applyCustom = (n, unit) => {
                      const v = parseInt(n, 10);
                      set('recurrence', v > 0 ? `every:${v}:${unit}` : '');
                      if (v > 0) track('task_recurrence_set', { recurrence: `every:${v}:${unit}`, custom: true });
                    };
                    return (
                      <>
                        <select
                          className="form-select"
                          value={isCustom ? 'custom' : (PRESETS.includes(form.recurrence) ? form.recurrence : '')}
                          onChange={e => {
                            const v = e.target.value;
                            if (v === 'custom') {
                              setRecCustomOpen(true);
                              if (!m) set('recurrence', '');
                              return;
                            }
                            setRecCustomOpen(false);
                            set('recurrence', v);
                            if (v) track('task_recurrence_set', { recurrence: v, custom: false });
                          }}
                        >
                          <option value="">{t('tasks.selectRecurrence')}</option>
                          <option value="weekly">{t('property.weekly')}</option>
                          <option value="monthly">{t('property.monthly')}</option>
                          <option value="quarterly">{t('property.quarterly')}</option>
                          <option value="yearly">{t('property.yearly')}</option>
                          <option value="custom">{t('tasks.recurrenceCustom')}</option>
                        </select>
                        {isCustom && (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8 }}>
                            <span style={{ fontSize: 13, color: 'var(--text2)' }}>{t('tasks.recurrenceEvery')}</span>
                            <input
                              autoFocus={!m}
                              type="number"
                              min={1}
                              max={999}
                              className="form-input"
                              style={{ width: 70, minHeight: 44 }}
                              value={customN}
                              data-ph-capture-attribute-field="task_recurrence_custom_n"
                              onChange={e => { setRecCustomN(e.target.value); applyCustom(e.target.value, customUnit); }}
                            />
                            <select
                              className="form-select"
                              style={{ flex: 1 }}
                              value={customUnit}
                              data-ph-capture-attribute-field="task_recurrence_custom_unit"
                              onChange={e => { setRecCustomUnit(e.target.value); applyCustom(customN, e.target.value); }}
                            >
                              <option value="d">{t('tasks.recurrenceUnit_d')}</option>
                              <option value="w">{t('tasks.recurrenceUnit_w')}</option>
                              <option value="m">{t('tasks.recurrenceUnit_m')}</option>
                              <option value="y">{t('tasks.recurrenceUnit_y')}</option>
                            </select>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            {showStatusField && form.type === 'recurring' && statusField}
            {isAwaitingForm && (
              <div className="form-group">
                <label className="form-label">{t('tasks.awaiting.followUpLabel')}</label>
                <input className="form-input" type="date" min={today} value={form.follow_up_date || ''}
                  data-ph-id="task-follow-up-date"
                  onChange={e => set('follow_up_date', e.target.value)} style={{ minHeight: 44 }} />
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>{t('tasks.awaiting.followUpHint')}</div>
              </div>
            )}
            {!isAwaitingForm && (<>
            {/* Тип планирования: Визит / Работа */}
            <div className="form-group" style={{ marginBottom: 4 }}>
              <label className="form-label">{t('tasks.fieldPlanningType')}</label>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                {['visit', 'work'].map(pt => (
                  <button
                    key={pt}
                    type="button"
                    onClick={() => {
                      set('planning_type', pt);
                      track('task_planning_type_changed', { planning_type: pt });
                    }}
                    style={{
                      flex: 1, padding: '8px 0', borderRadius: 8, border: '1.5px solid',
                      borderColor: form.planning_type === pt ? 'var(--accent)' : 'var(--border, #e0e0e0)',
                      background: form.planning_type === pt ? 'rgba(79,142,247,0.12)' : 'transparent',
                      color: form.planning_type === pt ? 'var(--accent)' : 'var(--text2)',
                      fontWeight: form.planning_type === pt ? 600 : 400,
                      fontSize: 14, cursor: 'pointer', transition: 'all 0.15s',
                    }}
                  >
                    {t(`tasks.planningType_${pt}`)}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: form.planning_type === 'visit' ? '1fr 1fr' : '1fr 1fr', gap: 12, alignItems: 'end' }}>
              {form.planning_type === 'visit' ? (
                <>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">{t('tasks.fieldDate')}</label>
                    <input className="form-input" type="date" value={form.scheduled_date || ''} onChange={e => set('scheduled_date', e.target.value)} style={{ minHeight: 44 }} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">{t('tasks.fieldTime')}</label>
                    <div ref={reminderRef} style={{ display: 'flex', gap: 6, alignItems: 'stretch', position: 'relative' }}>
                      <input
                        className="form-input"
                        type="time"
                        value={form.scheduled_time || ''}
                        onChange={e => set('scheduled_time', e.target.value)}
                        style={{ flex: 1, minWidth: 60, minHeight: 44 }}
                      />
                      {/* Кнопка напоминания — всегда видна, серая/неактивная без времени */}
                      <button
                        type="button"
                        onClick={() => {
                          if (!form.scheduled_time) return;
                          setReminderOpen(o => !o);
                          setReminderCustom(false);
                          track('task_reminder_bell_click');
                        }}
                        title={t('tasks.reminderBefore')}
                        style={{
                          flexShrink: 0, border: '1.5px solid var(--border)',
                          borderRadius: 8,
                          background: form.scheduled_time && form.reminder_before !== null ? 'var(--accent)' : 'var(--bg2)',
                          color: form.scheduled_time && form.reminder_before !== null ? 'white' : 'var(--text2)',
                          padding: '10px 10px', cursor: form.scheduled_time ? 'pointer' : 'default',
                          display: 'flex', alignItems: 'center', gap: 5,
                          fontSize: 12, fontWeight: 500, transition: 'all 0.15s',
                          opacity: form.scheduled_time ? 1 : 0.4,
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M8 2a4.5 4.5 0 0 0-4.5 4.5c0 2.1-.7 3.2-1.3 3.8h11.6c-.6-.6-1.3-1.7-1.3-3.8A4.5 4.5 0 0 0 8 2z"/>
                          <line x1="8" y1="2" x2="8" y2="1"/>
                          <path d="M6.5 13.5a1.5 1.5 0 0 0 3 0"/>
                          {(!form.scheduled_time || form.reminder_before === null) && <line x1="2" y1="2" x2="14" y2="14"/>}
                        </svg>
                        {form.scheduled_time && form.reminder_before !== null && (
                          <span>
                            {form.reminder_before < 60
                              ? `${form.reminder_before}м`
                              : `${Math.round(form.reminder_before / 60)}ч`}
                          </span>
                        )}
                      </button>
                      {/* Дропдаун */}
                      {reminderOpen && (
                        <div style={{
                          position: 'absolute', top: 'calc(100% + 4px)', right: 0,
                          background: 'var(--bg)', border: '1px solid var(--border)',
                          borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                          zIndex: 100, minWidth: 160, overflow: 'hidden',
                        }}>
                          {[
                            { label: t('tasks.reminderOff'),    value: null },
                            { label: t('tasks.reminder15min'),  value: 15 },
                            { label: t('tasks.reminder30min'),  value: 30 },
                            { label: t('tasks.reminder1h'),     value: 60 },
                            { label: t('tasks.reminder2h'),     value: 120 },
                            { label: t('tasks.reminder3h'),     value: 180 },
                            { label: t('tasks.reminderCustom'), value: 'custom' },
                          ].map(opt => (
                            <button
                              key={String(opt.value)}
                              type="button"
                              onClick={() => {
                                if (opt.value === 'custom') {
                                  setReminderCustom(true);
                                  return;
                                }
                                set('reminder_before', opt.value);
                                track('task_reminder_set', { minutes: opt.value });
                                setReminderOpen(false);
                                setReminderCustom(false);
                              }}
                              style={{
                                display: 'block', width: '100%', textAlign: 'left',
                                padding: '9px 14px', border: 'none', background:
                                  form.reminder_before === opt.value ? 'color-mix(in srgb, var(--accent) 10%, transparent)' : 'transparent',
                                color: form.reminder_before === opt.value ? 'var(--accent)' : 'var(--text)',
                                fontWeight: form.reminder_before === opt.value ? 600 : 400,
                                cursor: 'pointer', fontSize: 13,
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                          {reminderCustom && (
                            <div style={{ padding: '8px 14px', borderTop: '1px solid var(--border)', display: 'flex', gap: 6, alignItems: 'center' }}>
                              <input
                                autoFocus
                                type="number"
                                min={1}
                                max={1440}
                                placeholder={t('tasks.reminderCustomPlaceholder')}
                                className="form-input"
                                style={{ width: 70, padding: '4px 8px', fontSize: 13 }}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    const v = parseInt(e.target.value, 10);
                                    if (v > 0) {
                                      set('reminder_before', v);
                                      track('task_reminder_set', { minutes: v, custom: true });
                                      setReminderOpen(false);
                                      setReminderCustom(false);
                                    }
                                  }
                                }}
                              />
                              <span style={{ fontSize: 12, color: 'var(--text2)' }}>{t('tasks.reminderMinutes')}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">{t('tasks.fieldDateFrom')}</label>
                    <input className="form-input" type="date" value={form.date_from || ''} onChange={e => set('date_from', e.target.value)} onBlur={normalizeWorkDates} style={{ minHeight: 44 }} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">{t('tasks.fieldDeadline')}</label>
                    <input className="form-input" type="date" value={form.scheduled_date || ''} min={form.date_from || undefined} onChange={e => set('scheduled_date', e.target.value)} onBlur={normalizeWorkDates} style={{ minHeight: 44 }} />
                  </div>
                </>
              )}
            </div>
            </>)}

            {datesChanged && (
              <div className="form-group" style={{ marginTop: 8 }}>
                <label className="form-label">{t('tasks.dateChangeReason')}</label>
                <input className="form-input" type="text" maxLength={500} value={form.date_change_reason || ''}
                  onChange={e => set('date_change_reason', e.target.value)} title={form.date_change_reason || undefined}
                  placeholder={t('tasks.dateChangeReasonPh')} data-ph-capture="task_date_change_reason" />
              </div>
            )}

            {/* Таймер 1 (крупный масштаб) + «уведомлять клиента». Только визит, только админ.
                Сработавший таймер шлёт и исполнителям (push+email), и клиенту (email). */}
            {!isAwaitingForm && form.planning_type === 'visit' && !isWorker && (
              <div className="form-group" style={{ marginTop: 8 }}>
                <label className="form-label">{t('tasks.reminderEarly')}</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                  {[
                    { label: t('tasks.reminderOff'),    value: null },
                    { label: t('tasks.reminderWeek'),   value: 10080 },
                    { label: t('tasks.reminder3days'),  value: 4320 },
                    { label: t('tasks.reminder1day'),   value: 1440 },
                  ].map(opt => {
                    const active = (form.reminder_before_1 ?? null) === opt.value;
                    return (
                      <button key={String(opt.value)} type="button"
                        onClick={() => {
                          set('reminder_before_1', opt.value);
                          setCoarseCustom(false);
                          track('visit_reminder_configured', { timer: 1, value: opt.value, notify_client: !!form.notify_client });
                        }}
                        style={{
                          padding: '7px 12px', borderRadius: 8, border: '1.5px solid',
                          borderColor: active ? 'var(--accent)' : 'var(--border)',
                          background: active ? 'color-mix(in srgb, var(--accent) 12%, transparent)' : 'transparent',
                          color: active ? 'var(--accent)' : 'var(--text2)',
                          fontWeight: active ? 600 : 400, fontSize: 13, cursor: 'pointer',
                        }}>
                        {opt.label}
                      </button>
                    );
                  })}
                  {/* Своё (в днях) */}
                  <button type="button"
                    onClick={() => setCoarseCustom(c => !c)}
                    style={{
                      padding: '7px 12px', borderRadius: 8, border: '1.5px solid',
                      borderColor: coarseCustom ? 'var(--accent)' : 'var(--border)',
                      background: 'transparent', color: 'var(--text2)', fontSize: 13, cursor: 'pointer',
                    }}>
                    {t('tasks.reminderCustom')}
                  </button>
                  {coarseCustom && (
                    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                      <input autoFocus type="number" min={1} max={60}
                        className="form-input" style={{ width: 70, padding: '4px 8px', fontSize: 13 }}
                        placeholder={t('tasks.reminderCustomDaysPlaceholder')}
                        defaultValue={form.reminder_before_1 ? Math.round(form.reminder_before_1 / 1440) : ''}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const v = parseInt(e.target.value, 10);
                            if (v > 0) {
                              set('reminder_before_1', v * 1440);
                              track('visit_reminder_configured', { timer: 1, value: v * 1440, custom: true, notify_client: !!form.notify_client });
                              setCoarseCustom(false);
                            }
                          }
                        }} />
                      <span style={{ fontSize: 12, color: 'var(--text2)' }}>{t('tasks.reminderDays')}</span>
                    </span>
                  )}
                </div>

                <label style={{
                  display: 'flex', alignItems: 'center', gap: 8, marginTop: 12,
                  cursor: notifyClientDisabled ? 'not-allowed' : 'pointer',
                  opacity: notifyClientDisabled ? 0.5 : 1,
                }}>
                  <input type="checkbox"
                    disabled={notifyClientDisabled}
                    checked={!notifyClientDisabled && form.notify_client !== false && form.notify_client !== 0}
                    onChange={e => { set('notify_client', e.target.checked); track('visit_reminder_configured', { timer: 'notify_client', value: e.target.checked }); }} />
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{t('tasks.notifyClient')}</span>
                </label>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
                  {notifyClientDisabled ? (
                    <>
                      {t('tasks.notifyClientNoEmail')}{' '}
                      {clientId && (
                        <button type="button" onClick={openClientEditor}
                          style={{
                            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                            color: 'var(--accent)', fontWeight: 600, fontSize: 12, textDecoration: 'underline',
                          }}>
                          {t('tasks.notifyClientAddEmail')}
                        </button>
                      )}
                    </>
                  ) : t('tasks.notifyClientHint')}
                </div>
              </div>
            )}


            {/* Исполнители */}
            <div className="form-group" style={{ marginTop: 4 }}>
              <label className="form-label">{t('tasks.fieldAssignees')}</label>
              {isWorker ? (
                // Работник видит назначенных из task.assignees; при создании — себя
                (() => {
                  const assignees = task?.assignees || (!task?.id && user ? [{ id: user.id, name: user.name, role: user.role }] : []);
                  return assignees.length === 0 ? (
                    <div style={{ fontSize: 13, color: 'var(--text2)', padding: '8px 0' }}>
                      {t('tasks.noAssignees')}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                      {assignees.map(member => (
                        <div key={member.id} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 12px',
                        border: '1.5px solid var(--accent)',
                        borderRadius: 8,
                        background: 'rgba(79,142,247,0.10)',
                      }}>
                        <UserCircle
                          name={member.name}
                          avatarUrl={member.avatar_thumb_url}
                          size={32}
                          style={{ background: member.avatar_thumb_url ? 'transparent' : 'var(--accent)', color: '#fff' }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500, fontSize: 14 }}>{member.name}</div>
                          <div style={{ fontSize: 12, color: 'var(--text2)', opacity: 0.8 }}>
                            {ROLE_LABELS[member.role] || member.role}
                          </div>
                        </div>
                        <span style={{ color: 'var(--accent)', fontSize: 16, flexShrink: 0 }}>✓</span>
                      </div>
                    ))}
                  </div>
                  );
                })()
              ) : (
                <AssigneePicker
                  value={selectedIds}
                  onChange={(ids) => setForm(f => ({ ...f, assignee_ids: ids }))}
                  propertyId={propertyId || form.property_id || task?.property_id || null}
                  knownUsers={task?.assignees || []}
                  onLoaded={() => setAssignableLoaded(true)}
                />
              )}
            </div>

            {/* Коммуникации с клиентом: перенесены в отдельный флоу */}

          </div>
          <div className="modal-footer">
            {task?.id && workerCanEditMeta && onDelete && (
              <button type="button" className="btn btn-danger" style={{ marginRight: 'auto' }}
                onClick={() => onDelete(task)}>
                🗑️ {t('common.delete')}
              </button>
            )}
            <button type="button" className="btn btn-secondary" onClick={handleCancel}>{t('common.cancel')}</button>
            <button type="submit" className="btn btn-primary" disabled={loading}
              ref={demoSubmitRef}
              {...(demoMode ? { 'data-demo-target': 'demo-assign-btn' } : {})}>
              {loading ? <span className="spinner" /> : (demoMode && demoSubmitLabel ? demoSubmitLabel : t('common.save'))}
            </button>
          </div>
        </form>

        {emailComposer && (
          <ClientEmailComposer
            task={task}
            template={emailComposer}
            onClose={() => setEmailComposer(null)}
            onSent={() => setEmailComposer(null)}
          />
        )}

        {editClient && (
          <ClientModal
            client={editClient}
            focusField="email"
            onClose={() => setEditClient(null)}
            onSave={(updated) => {
              // Email добавлен — разблокируем тумблер «Уведомлять клиента» и сразу ставим галочку.
              if (updated && typeof updated.email !== 'undefined') {
                setResolvedClientEmail(updated.email || null);
                if (updated.email) set('notify_client', true);
              }
              setEditClient(null);
            }}
          />
        )}
      </div>
    </div>
  );
}
